// src/pages/RevenueEnginePage.jsx
// 💰 Revenue Engine — outcome attribution + recovery analytics
//
// Answers: "Which action recovered which dollar?"
// Aggregates payments (both portal and manual) with attribution data,
// shows channel performance, message leaderboard, and recovery KPIs.
//
// Data sources:
//   - Firestore: payments/{paymentId}  (attributed payments via portal)
//   - outreachLog entries with recoveryEvent:true  (manual + portal)
//   - outreachLog entries (all channels) for "touches" count

import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Cell, PieChart, Pie,
} from 'recharts';
import {
  collection, getDocs, query, where, orderBy, limit,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useProperty } from '../hooks/useProperty';
import { useTenants } from '../hooks/useTenants';
import { formatCurrency } from '../utils/formatters';

// ── Brand palette ─────────────────────────────────────────────────────────────
const BLUE   = '#2563EB';
const PURPLE = '#7C3AED';
const GREEN  = '#16A34A';
const AMBER  = '#F59E0B';
const RED    = '#DC2626';

const CHANNEL_COLORS = {
  sms:          BLUE,
  email:        PURPLE,
  voice:        AMBER,
  payment_plan: GREEN,
  manual:       '#6B7280',
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function buildMonthLabels(n = 6) {
  const months = [];
  const now = new Date();
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      key:   `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
    });
  }
  return months;
}

function thisMonthKey() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

// ── KPI Card ──────────────────────────────────────────────────────────────────
function KpiCard({ label, value, sub, icon, color = BLUE, trend }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
      <div className="flex items-start justify-between mb-3">
        <span className="text-2xl">{icon}</span>
        {trend !== undefined && (
          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
            trend >= 0 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}>
            {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}%
          </span>
        )}
      </div>
      <p className="text-2xl font-bold text-gray-900 leading-tight">{value}</p>
      <p className="text-sm text-gray-500 mt-0.5">{label}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

// ── Attribution badge ─────────────────────────────────────────────────────────
function AttributionBadge({ type }) {
  const styles = {
    direct:   'bg-green-50 text-green-700 border-green-200',
    window:   'bg-blue-50 text-blue-700 border-blue-200',
    weighted: 'bg-purple-50 text-purple-700 border-purple-200',
    manual:   'bg-gray-50 text-gray-600 border-gray-200',
    portal:   'bg-blue-50 text-blue-700 border-blue-200',
    none:     'bg-gray-50 text-gray-400 border-gray-100',
  };
  const labels = {
    direct: '⚡ Direct', window: '⏱ Window', weighted: '⚖ Weighted',
    manual: '✋ Manual', portal: '🔗 Portal', none: '— Unattributed',
  };
  return (
    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${styles[type] || styles.none}`}>
      {labels[type] || type}
    </span>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function RevenueEnginePage() {
  const { selectedProperty: property } = useProperty();
  const { tenants, loading: tenantsLoading } = useTenants(property?.id);

  // Portal payments from the top-level payments/ collection
  const [portalPayments, setPortalPayments]   = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(true);

  const months = useMemo(() => buildMonthLabels(6), []);
  const MTD    = thisMonthKey();

  // ── Load payments/ collection ─────────────────────────────────────────────
  useEffect(() => {
    if (!property?.id) return;
    const q = query(
      collection(db, 'payments'),
      where('propertyId', '==', property.id),
      orderBy('createdAt', 'desc'),
      limit(200)
    );
    getDocs(q)
      .then(snap => {
        setPortalPayments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setPaymentsLoading(false);
      })
      .catch(err => {
        console.error('Error loading payments:', err);
        setPaymentsLoading(false);
      });
  }, [property?.id]);

  // ── Compute analytics from tenants outreachLog ────────────────────────────
  const analytics = useMemo(() => {
    if (tenantsLoading || !tenants.length) return null;

    // ── Flatten all outreach log entries ──────────────────────────────────────
    const allEntries = [];
    tenants.forEach(t => {
      (t.outreachLog || []).forEach(e => {
        allEntries.push({ ...e, tenantId: t.id, tenantName: t.name });
      });
    });

    // ── Payment / recovery events ─────────────────────────────────────────────
    const recoveryEvents = allEntries.filter(e => e.recoveryEvent && e.amount > 0);
    const mtdRecovery    = recoveryEvents.filter(e => (e.date || '').slice(0, 7) === MTD);
    const totalRecoveredMTD = mtdRecovery.reduce((s, e) => s + (e.amount || 0), 0);
    const totalRecoveredAll = recoveryEvents.reduce((s, e) => s + (e.amount || 0), 0);

    // ── Portal-attributed payments (from payments/ collection) ───────────────
    const attributedPayments  = portalPayments.filter(p => p.attributedMessageId);
    const attributedDollarsMTD = portalPayments
      .filter(p => (p.createdAt || '').slice(0, 7) === MTD)
      .reduce((s, p) => s + (p.amount || 0), 0);

    // ── Avg hours to payment (from portal payments that have it) ─────────────
    const withTTP = portalPayments.filter(p => p.timeToPayHours !== null && p.timeToPayHours !== undefined);
    const avgHoursToPayment = withTTP.length
      ? (withTTP.reduce((s, p) => s + p.timeToPayHours, 0) / withTTP.length).toFixed(1)
      : null;

    // ── Recovery by channel ───────────────────────────────────────────────────
    // Count touches and dollars by channel
    const channelMap = {};
    allEntries.forEach(e => {
      const ch = e.channel || 'unknown';
      if (!channelMap[ch]) channelMap[ch] = { channel: ch, touches: 0, dollarsSent: 0, recovered: 0, payments: 0 };
      channelMap[ch].touches++;
    });
    recoveryEvents.forEach(e => {
      // Attribute payment to the channel that preceded it (if source is 'portal', use attributedChannel)
      const ch = e.attributedChannel || e.channel || 'unknown';
      if (channelMap[ch]) {
        channelMap[ch].recovered += e.amount || 0;
        channelMap[ch].payments++;
      }
    });
    const byChannel = Object.values(channelMap)
      .filter(c => c.channel !== 'payment' && c.channel !== 'payment_plan')
      .map(c => ({
        ...c,
        label: c.channel.toUpperCase(),
        convRate: c.touches > 0 ? ((c.payments / c.touches) * 100).toFixed(1) : '0.0',
      }))
      .sort((a, b) => b.recovered - a.recovered);

    // ── Best channel by dollars recovered ─────────────────────────────────────
    const bestChannel = byChannel.length ? byChannel[0] : null;

    // ── Recovery trend over last 6 months ─────────────────────────────────────
    const recoveryTrend = months.map(m => {
      const monthEvents = recoveryEvents.filter(e => (e.date || '').slice(0, 7) === m.key);
      const recovered   = monthEvents.reduce((s, e) => s + (e.amount || 0), 0);
      return { label: m.label, recovered };
    });

    // ── Message template leaderboard ─────────────────────────────────────────
    // Group outreach entries (sent messages) by strategy-type (template)
    const templateMap = {};
    allEntries
      .filter(e => e.channel === 'sms' || e.channel === 'email' || e.channel === 'voice')
      .forEach(e => {
        const key = e.templateKey || e.sentBy || 'sms';
        if (!templateMap[key]) templateMap[key] = { template: key, sent: 0, recovered: 0, avgTTP: [], payments: 0 };
        templateMap[key].sent++;
      });
    // Try to attribute portal payments to message templates
    portalPayments.forEach(p => {
      if (!p.attributedMessageId) return;
      // Find the originating outreach entry
      const origEntry = allEntries.find(e => e.messageId === p.attributedMessageId);
      if (!origEntry) return;
      const key = origEntry.templateKey || origEntry.sentBy || 'sms';
      if (templateMap[key]) {
        templateMap[key].recovered += p.amount || 0;
        templateMap[key].payments++;
        if (p.timeToPayHours !== null && p.timeToPayHours !== undefined) {
          templateMap[key].avgTTP.push(p.timeToPayHours);
        }
      }
    });
    const templateLeaderboard = Object.values(templateMap)
      .map(t => ({
        ...t,
        convRate: t.sent > 0 ? ((t.payments / t.sent) * 100).toFixed(1) : '0.0',
        avgTTPHours: t.avgTTP.length
          ? (t.avgTTP.reduce((s, h) => s + h, 0) / t.avgTTP.length).toFixed(1)
          : null,
      }))
      .sort((a, b) => b.recovered - a.recovered)
      .slice(0, 10);

    // ── Touch-to-payment funnel ───────────────────────────────────────────────
    const totalTouches     = allEntries.filter(e => e.channel !== 'payment').length;
    const totalReplied     = allEntries.filter(e => e.responseText).length;
    const totalPaid        = recoveryEvents.length;
    const totalAttributed  = attributedPayments.length;

    // ── All recent payments list (for the table) ──────────────────────────────
    const recentPayments = [...recoveryEvents]
      .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0))
      .slice(0, 20)
      .map(e => ({
        ...e,
        attribution: e.source === 'portal'
          ? (e.attributedMessageId ? 'direct' : 'portal')
          : 'manual',
      }));

    return {
      totalRecoveredMTD,
      totalRecoveredAll,
      attributedDollarsMTD,
      avgHoursToPayment,
      byChannel,
      bestChannel,
      recoveryTrend,
      templateLeaderboard,
      totalTouches,
      totalReplied,
      totalPaid,
      totalAttributed,
      attributedPayments,
      recentPayments,
    };
  }, [tenants, tenantsLoading, portalPayments, months, MTD]);

  const loading = tenantsLoading || paymentsLoading;

  // ── Loading state ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center gap-3 text-gray-400">
          <div className="w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
          <span>Loading Revenue Engine…</span>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 text-gray-500">
        No property selected.{' '}
        <Link to="/settings" className="text-blue-600 underline">Go to Settings</Link>
      </div>
    );
  }

  const a = analytics;
  const noData = !a || (a.totalTouches === 0 && a.totalPaid === 0);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">

      {/* ── Page header ──────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            💰 Revenue Engine
          </h1>
          <p className="text-gray-500 mt-0.5 text-sm">
            Attribution · Recovery · Performance — {property?.name}
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/reports"
            className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-xl hover:border-blue-400 hover:text-blue-600 transition-colors">
            ← Reports
          </Link>
        </div>
      </div>

      {/* ── Empty state ───────────────────────────────────────────────────── */}
      {noData && (
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-8 text-center">
          <div className="text-4xl mb-3">📊</div>
          <h2 className="font-semibold text-gray-900 mb-2">Attribution data will appear here</h2>
          <p className="text-sm text-gray-500 max-w-md mx-auto">
            Once you start sending outreach with trackable payment links and tenants make payments,
            you'll see exactly which messages drove which dollars.
          </p>
          <Link to="/"
            className="inline-block mt-4 px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors">
            Go to Action Feed →
          </Link>
        </div>
      )}

      {a && !noData && (
        <>
          {/* ── KPI row ────────────────────────────────────────────────────── */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              icon="💵"
              label="Recovered This Month"
              value={formatCurrency(a.totalRecoveredMTD)}
              sub="from manual + portal payments"
              color={BLUE}
            />
            <KpiCard
              icon="🔗"
              label="Attributed via Portal"
              value={formatCurrency(a.attributedDollarsMTD)}
              sub={`${a.attributedPayments.length} payment${a.attributedPayments.length !== 1 ? 's' : ''} tracked`}
              color={PURPLE}
            />
            <KpiCard
              icon="⏱"
              label="Avg Hours to Payment"
              value={a.avgHoursToPayment !== null ? `${a.avgHoursToPayment}h` : '—'}
              sub="from message sent to payment"
              color={AMBER}
            />
            <KpiCard
              icon="📱"
              label={a.bestChannel ? `Best Channel: ${a.bestChannel.channel.toUpperCase()}` : 'Best Channel'}
              value={a.bestChannel ? formatCurrency(a.bestChannel.recovered) : '—'}
              sub={a.bestChannel ? `${a.bestChannel.convRate}% conv · ${a.bestChannel.touches} touches` : 'No channel data yet'}
              color={GREEN}
            />
          </div>

          {/* ── Attribution funnel ────────────────────────────────────────── */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <h2 className="font-semibold text-gray-900 mb-4">Touch → Payment Funnel</h2>
            <div className="grid grid-cols-4 gap-3">
              {[
                { label: 'Messages Sent',  value: a.totalTouches,    icon: '📤', pct: 100 },
                { label: 'Replied',        value: a.totalReplied,    icon: '💬', pct: a.totalTouches > 0 ? Math.round((a.totalReplied / a.totalTouches) * 100) : 0 },
                { label: 'Payments Made',  value: a.totalPaid,       icon: '✅', pct: a.totalTouches > 0 ? Math.round((a.totalPaid / a.totalTouches) * 100) : 0 },
                { label: 'Attributed',     value: a.totalAttributed, icon: '🔗', pct: a.totalPaid > 0 ? Math.round((a.totalAttributed / a.totalPaid) * 100) : 0 },
              ].map((step, i) => (
                <div key={i} className="text-center">
                  <div className="text-2xl mb-1">{step.icon}</div>
                  <div className="text-xl font-bold text-gray-900">{step.value.toLocaleString()}</div>
                  <div className="text-xs text-gray-500 mb-2">{step.label}</div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${step.pct}%`,
                        background: `linear-gradient(90deg, ${BLUE}, ${PURPLE})`,
                      }}
                    />
                  </div>
                  <div className="text-xs text-gray-400 mt-1">{step.pct}%</div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Charts row ────────────────────────────────────────────────── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* Recovery trend */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
              <h2 className="font-semibold text-gray-900 mb-4">Recovery Trend (6 months)</h2>
              {a.recoveryTrend.every(m => m.recovered === 0) ? (
                <div className="h-40 flex items-center justify-center text-gray-400 text-sm">
                  No payments recorded yet
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={a.recoveryTrend} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} width={50} />
                    <Tooltip formatter={v => formatCurrency(v)} />
                    <Line
                      type="monotone"
                      dataKey="recovered"
                      stroke={BLUE}
                      strokeWidth={2.5}
                      dot={{ fill: BLUE, r: 4 }}
                      name="Recovered"
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Recovery by channel */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
              <h2 className="font-semibold text-gray-900 mb-4">Recovery by Channel</h2>
              {a.byChannel.length === 0 ? (
                <div className="h-40 flex items-center justify-center text-gray-400 text-sm">
                  No channel data yet
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={a.byChannel} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} width={50} />
                    <Tooltip
                      formatter={(v, name) => name === 'recovered' ? [formatCurrency(v), 'Recovered'] : [v, name]}
                    />
                    <Bar dataKey="recovered" name="Recovered" radius={[4, 4, 0, 0]}>
                      {a.byChannel.map((entry) => (
                        <Cell key={entry.channel} fill={CHANNEL_COLORS[entry.channel] || BLUE} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}

              {/* Channel stats table */}
              {a.byChannel.length > 0 && (
                <div className="mt-4 divide-y divide-gray-100">
                  {a.byChannel.map(ch => (
                    <div key={ch.channel} className="flex items-center justify-between py-2 text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ background: CHANNEL_COLORS[ch.channel] || BLUE }} />
                        <span className="font-medium text-gray-700 capitalize">{ch.channel}</span>
                        <span className="text-gray-400">{ch.touches} touches</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-gray-500">{ch.convRate}% conv</span>
                        <span className="font-semibold text-gray-900">{formatCurrency(ch.recovered)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── Template leaderboard ──────────────────────────────────────── */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <h2 className="font-semibold text-gray-900 mb-1">Message Performance Leaderboard</h2>
            <p className="text-xs text-gray-400 mb-4">Ranked by dollars recovered. Attribution improves as more tenants use payment links.</p>
            {a.templateLeaderboard.length === 0 ? (
              <p className="text-gray-400 text-sm">No message data yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider border-b border-gray-100">
                      <th className="pb-2 pr-4">#</th>
                      <th className="pb-2 pr-4">Strategy / Template</th>
                      <th className="pb-2 pr-4 text-right">Sent</th>
                      <th className="pb-2 pr-4 text-right">Payments</th>
                      <th className="pb-2 pr-4 text-right">Conv %</th>
                      <th className="pb-2 pr-4 text-right">Avg TTP</th>
                      <th className="pb-2 text-right">Recovered</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {a.templateLeaderboard.map((t, i) => (
                      <tr key={t.template} className="hover:bg-gray-50">
                        <td className="py-3 pr-4 text-gray-400 font-mono">{i + 1}</td>
                        <td className="py-3 pr-4 font-medium text-gray-800 capitalize">{t.template}</td>
                        <td className="py-3 pr-4 text-right text-gray-600">{t.sent}</td>
                        <td className="py-3 pr-4 text-right text-gray-600">{t.payments}</td>
                        <td className="py-3 pr-4 text-right">
                          <span className={`font-semibold ${parseFloat(t.convRate) > 10 ? 'text-green-600' : 'text-gray-700'}`}>
                            {t.convRate}%
                          </span>
                        </td>
                        <td className="py-3 pr-4 text-right text-gray-500">
                          {t.avgTTPHours !== null ? `${t.avgTTPHours}h` : '—'}
                        </td>
                        <td className="py-3 text-right font-semibold text-gray-900">
                          {t.recovered > 0 ? formatCurrency(t.recovered) : <span className="text-gray-300">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ── Recent payments with attribution ──────────────────────────── */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <h2 className="font-semibold text-gray-900 mb-1">Recent Payments</h2>
            <p className="text-xs text-gray-400 mb-4">Last 20 payment events across all channels.</p>
            {a.recentPayments.length === 0 ? (
              <p className="text-gray-400 text-sm">No payments recorded yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider border-b border-gray-100">
                      <th className="pb-2 pr-4">Tenant</th>
                      <th className="pb-2 pr-4">Amount</th>
                      <th className="pb-2 pr-4">Date</th>
                      <th className="pb-2 pr-4">Source</th>
                      <th className="pb-2">Attribution</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {a.recentPayments.map((p, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="py-3 pr-4 font-medium text-gray-800">{p.tenantName || '—'}</td>
                        <td className="py-3 pr-4 font-semibold text-green-600">{formatCurrency(p.amount)}</td>
                        <td className="py-3 pr-4 text-gray-500">
                          {p.date ? new Date(p.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                        </td>
                        <td className="py-3 pr-4 text-gray-500 capitalize">{p.source || 'manual'}</td>
                        <td className="py-3">
                          <AttributionBadge type={
                            p.source === 'portal' && p.attributedMessageId
                              ? 'portal'
                              : p.source === 'manual'
                              ? 'manual'
                              : 'none'
                          } />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ── Attribution info box ──────────────────────────────────────── */}
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
            <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
              🧠 Building Your Attribution Model
            </h3>
            <p className="text-sm text-blue-800 leading-relaxed mb-3">
              Every SMS sent now includes a unique payment link. When tenants click that link and pay,
              the payment is directly attributed to that exact message — including channel, timing, and template.
            </p>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div className="bg-white rounded-xl p-3 border border-blue-100">
                <div className="font-semibold text-green-600 mb-0.5">⚡ Direct</div>
                <div className="text-gray-500 text-xs">Tenant clicked link → paid</div>
              </div>
              <div className="bg-white rounded-xl p-3 border border-blue-100">
                <div className="font-semibold text-blue-600 mb-0.5">⏱ Window</div>
                <div className="text-gray-500 text-xs">Paid within 24h of message</div>
              </div>
              <div className="bg-white rounded-xl p-3 border border-blue-100">
                <div className="font-semibold text-purple-600 mb-0.5">⚖ Weighted</div>
                <div className="text-gray-500 text-xs">Multi-touch attribution</div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
